import { useEffect, useState } from "react";
import { ApiError, type OrgSubscriptionView, type PlanSwitchOptionsView } from "@keydials/api-client";
import { api, getAccessToken } from "../auth/AuthContext";
import { DiscountCodeField } from "./DiscountCodeField";
import { SettingsCard } from "./SettingsCard";
import { formatSubscriptionDate, summariseSubscription } from "./subscriptionSummary";
import { switchOptionLabel, switchTiming, switchedNote } from "./planSwitch";

/**
 * The organisation's plan, in Your settings (KEY-635).
 *
 * Until this existed a customer could see a plan name and a seat count in a dial's Licensing panel and
 * nothing else — no status, no trial end date, and no way to add a card, change one, read an invoice or
 * cancel. The buy page promised "you can change or cancel from your account at any time", the trial-ending
 * email said "subscribe at the app", and neither was true. This is the card those sentences point at.
 *
 * Two buttons, and which ones show is decided by the server's view of the subscription, not here:
 * "Subscribe now" while a card-free trial runs (the checkout keeps the trial's end date, KEY-633) or has
 * expired; "Manage billing" whenever Stripe is billing this, which opens Stripe's own portal. Everything
 * the portal does comes back to us as a webhook, so there is nothing to mirror in-app.
 */
export function SubscriptionCard({
  organisationId,
  email,
  onPlanChanged,
}: {
  organisationId: string;
  email: string;
  /** Told after the plan switches, so a card that depends on the plan (Family) can read it again. */
  onPlanChanged?: () => void;
}) {
  const [subscription, setSubscription] = useState<OrgSubscriptionView | null | undefined>(undefined);
  const [busy, setBusy] = useState<"subscribe" | "portal" | "switch" | null>(null);
  const [note, setNote] = useState<string | null>(null);
  /**
   * Rungs the plan can move to from here (KEY-651 phase 2) — single ↔ family, monthly ↔ annual — or null when it
   * can't be switched here. The server decides; the card only offers what it is given.
   */
  const [switchOptions, setSwitchOptions] = useState<PlanSwitchOptionsView | null>(null);
  const [switching, setSwitching] = useState(false);
  const [target, setTarget] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  /**
   * A discount code for "Subscribe now" (KEY-642). Optional: a trial sold on a code keeps its discount
   * without retyping it — the server carries it onto the new subscription.
   */
  const [discountCode, setDiscountCode] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const token = getAccessToken();
    if (!token) return;
    api
      .getOrgSubscription(token, organisationId)
      .then((view) => !cancelled && setSubscription(view))
      .catch(() => !cancelled && setSubscription(null));
    api
      .getPlanSwitchOptions(token, organisationId)
      .then((view) => !cancelled && setSwitchOptions(view))
      .catch(() => !cancelled && setSwitchOptions(null));
    return () => {
      cancelled = true;
    };
  }, [organisationId]);

  const switchPlan = async () => {
    const token = getAccessToken();
    const option = switchOptions?.options.find((o) => o.planId === target);
    if (!token || !option) return;
    setBusy("switch");
    setNote(null);
    try {
      const result = await api.switchPlan(token, organisationId, option.planId);
      setDone(switchedNote(result, option.name));
      setSwitching(false);
      setTarget(null);
      const [view, options] = await Promise.all([
        api.getOrgSubscription(token, organisationId),
        api.getPlanSwitchOptions(token, organisationId).catch(() => null),
      ]);
      setSubscription(view);
      setSwitchOptions(options);
      onPlanChanged?.();
    } catch (e) {
      setNote(e instanceof ApiError && e.message ? e.message : "Couldn't change your plan.");
    } finally {
      setBusy(null);
    }
  };

  const subscribeNow = async () => {
    const token = getAccessToken();
    if (!token || !subscription) return;
    setBusy("subscribe");
    setNote(null);
    try {
      const { url } = await api.startOrgCheckout(token, organisationId, {
        planId: subscription.planId,
        email,
        discountCode,
      });
      window.location.assign(url);
    } catch (e) {
      setNote(e instanceof ApiError && e.message ? e.message : "Couldn't start the checkout.");
      setBusy(null);
    }
  };

  const manageBilling = async () => {
    const token = getAccessToken();
    if (!token) return;
    setBusy("portal");
    setNote(null);
    try {
      const url = await api.getOrgPortalLink(token, organisationId);
      if (!url) {
        setNote("There's nothing to manage yet — this plan isn't billed by card.");
        setBusy(null);
        return;
      }
      window.location.assign(url);
    } catch (e) {
      setNote(e instanceof ApiError && e.message ? e.message : "Couldn't open billing.");
      setBusy(null);
    }
  };

  if (subscription === undefined) {
    return (
      <SettingsCard title="Subscription" hint="Your organisation's plan, and how it is paid for.">
        <div style={{ fontSize: 13, color: "var(--text-faint)" }}>Loading…</div>
      </SettingsCard>
    );
  }

  if (subscription === null) {
    return (
      <SettingsCard title="Subscription" hint="Your organisation's plan, and how it is paid for.">
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
          Your organisation isn't on a plan. If that's unexpected, get in touch with whoever set it up.
        </div>
      </SettingsCard>
    );
  }

  const summary = summariseSubscription(subscription);
  const canSwitch = (switchOptions?.options.length ?? 0) > 0;
  const chosen = switchOptions?.options.find((o) => o.planId === target) ?? null;

  return (
    <SettingsCard title="Subscription" hint="Your organisation's plan, and how it is paid for.">
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: "var(--text)" }}>{summary.name}</span>
            <span
              style={{
                fontSize: 12, fontWeight: 600, padding: "2px 8px", borderRadius: 999,
                background: "var(--accent-soft)", color: "var(--accent-text)",
              }}
            >
              {summary.status}
            </span>
          </div>
          <div style={{ marginTop: 3, fontSize: 13.5, color: "var(--text-muted)" }}>{summary.price}</div>
        </div>

        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: "var(--text-muted)" }}>{summary.detail}</p>

        {summary.dates.length > 0 && (
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 13 }}>
            {summary.dates.map((d) => (
              <div key={d.label}>
                <div style={{ color: "var(--text-muted)", fontSize: 12 }}>{d.label}</div>
                <div style={{ color: "var(--text)", fontWeight: 600 }}>{formatSubscriptionDate(d.iso)}</div>
              </div>
            ))}
          </div>
        )}

        {summary.canSubscribeNow && (
          <DiscountCodeField
            planId={subscription.planId}
            period={subscription.period}
            fullPricePence={subscription.amountPence}
            disabled={busy !== null}
            onChange={setDiscountCode}
          />
        )}

        {(summary.canSubscribeNow || summary.canManageBilling || canSwitch) && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {summary.canSubscribeNow && (
              <button className="kd-btn-primary kd-btn-sm" onClick={() => void subscribeNow()} disabled={busy !== null}>
                {busy === "subscribe" ? "Taking you to checkout…" : subscription.status === "TrialExpired"
                    ? "Subscribe"
                    : subscription.status === "Canceled"
                      ? "Subscribe again"
                      : "Add a card"}
              </button>
            )}
            {summary.canManageBilling && (
              <button className="kd-btn kd-btn-sm" onClick={() => void manageBilling()} disabled={busy !== null}>
                {busy === "portal" ? "Opening…" : "Manage billing"}
              </button>
            )}
            {canSwitch && !switching && (
              <button
                className="kd-btn kd-btn-sm"
                onClick={() => {
                  setSwitching(true);
                  setDone(null);
                }}
                disabled={busy !== null}
              >
                Change plan
              </button>
            )}
            <span style={{ fontSize: 12.5, color: "var(--text-faint)" }}>
              {summary.canManageBilling
                ? "Change your card, see invoices or cancel — on Stripe, which never shares your card with us."
                : "Card details are taken by Stripe and never touch this app."}
            </span>
          </div>
        )}

        {switching && switchOptions && (
          <fieldset style={{ margin: 0, padding: "12px 14px", border: "var(--border-w) solid var(--border)", borderRadius: "var(--radius-sm)", display: "flex", flexDirection: "column", gap: 10 }}>
            <legend style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", padding: "0 4px" }}>
              Change from {switchOptions.currentName} to…
            </legend>
            {switchOptions.options.map((option) => (
              <label key={option.planId} style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="kd-plan-switch"
                  checked={target === option.planId}
                  disabled={busy !== null}
                  onChange={() => setTarget(option.planId)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  <span style={{ display: "block", fontSize: 14, fontWeight: 600, color: "var(--text)" }}>{option.name}</span>
                  <span style={{ display: "block", fontSize: 12.5, color: "var(--text-muted)" }}>{switchOptionLabel(option)}</span>
                </span>
              </label>
            ))}
            {chosen && (
              <>
                <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: "var(--text-muted)" }}>{switchTiming(chosen)}</p>
                {chosen.warning && (
                  <p role="alert" style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: "var(--st-amber, #b45309)" }}>
                    {chosen.warning}
                  </p>
                )}
              </>
            )}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="kd-btn-primary kd-btn-sm" onClick={() => void switchPlan()} disabled={busy !== null || !chosen}>
                {busy === "switch" ? "Changing…" : chosen ? `Switch to ${chosen.name}` : "Choose a plan"}
              </button>
              <button
                className="kd-btn kd-btn-sm"
                onClick={() => {
                  setSwitching(false);
                  setTarget(null);
                }}
                disabled={busy !== null}
              >
                Keep my plan
              </button>
            </div>
          </fieldset>
        )}

        {done && <div role="status" style={{ fontSize: 13, color: "var(--text-muted)" }}>{done}</div>}
        {note && <div role="status" style={{ fontSize: 13, color: "var(--st-overdue)" }}>{note}</div>}
      </div>
    </SettingsCard>
  );
}
